import { useState, useMemo, useCallback, useEffect } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { Newspaper, ChevronLeft, ChevronRight, ChevronDown, ChevronUp, Calendar, Building2, Sparkles, Globe, Archive, ExternalLink, GitBranch, CheckSquare, Square, CheckCircle2, Undo2, Trash2, Send, GitPullRequest, Loader2, ShieldAlert, Search, X as XIcon, RefreshCw } from 'lucide-react';
import { getAnnouncementsWithArticles, archiveArticle, deleteArticle, approveArticle, promoteArticle } from '../lib/api/index.js';
import { unpublishArticle, publishArticle } from '../lib/api/egress.js';
import { rejudgeArticle } from '../lib/api/qg-actions.js';
import { QualityGateOverrideDialog } from '../components/QualityGateOverrideDialog.js';
import { apiClient } from '../lib/api/config.js';
import { dashboardQueryOptions, getDashboard, type DashboardJob } from '../lib/api/dashboard.js';
import { useWebSocket } from '../hooks/useWebSocket.js';
import { EntityGroupFilter, useEntityGroupPill } from '../components/EntityGroupFilter.js';
import { DateBar } from '../components/DateBar.js';
import { getArticleLaneDateCounts, ARTICLE_LANES, type ArticleLane } from '../lib/api/articles.js';
import { CoverageBadges } from '../components/CoverageBadges.js';
import { format } from 'date-fns';
import type { ArticleWithContext } from '../lib/api/types.js';
import { Modal, ModalBody, ModalFooter } from '../components/ui/modal.js';

// Per-entity and direct-input items always have an announcement; only the
// synthesis lanes (sitewide, syntheses) carry announcement=null.
type AnnouncedArticle = ArticleWithContext & { announcement: NonNullable<ArticleWithContext['announcement']> };
const hasAnnouncement = (it: ArticleWithContext): it is AnnouncedArticle => it.announcement != null;

const LANE_LABELS: Record<ArticleLane, string> = {
  per_entity: 'Per-entity',
  sitewide: 'Site-wide briefs',
  syntheses: 'Syntheses',
  direct_input: 'Direct input',
};

interface EntityGroup {
  entityKey: string;
  entityLabel: string;
  companyName: string;
  latestDate: string;
  articles: AnnouncedArticle[];
}

// Map pipeline_jobs rows into a per-article status map so the Articles page
// can surface a live "Publishing…"/"Unpublishing…"/"Promoting…" chip while
// jobs are in flight. Keyed by article_id (extracted from payload JSONB).
interface ArticleJobInfo {
  job_type: string;
  status: string;
  label: string;
  tone: 'publish' | 'unpublish' | 'promote' | 'other';
}

const JOB_LABELS: Record<string, { running: string; pending: string; tone: ArticleJobInfo['tone'] }> = {
  publish: { running: 'Publishing…', pending: 'Publish queued', tone: 'publish' },
  unpublish: { running: 'Unpublishing…', pending: 'Unpublish queued', tone: 'unpublish' },
  promote: { running: 'Promoting…', pending: 'Promote queued', tone: 'promote' },
  // promote_article fires when /articles/{id}/promote is called — the
  // backend now enqueues a worker job rather than running synchronously,
  // so the article gets a live progress badge through the existing
  // dashboard-job stream.
  promote_article: { running: 'Promoting…', pending: 'Promote queued', tone: 'promote' },
};

// Collapse publish badges so a target that's been promoted to main shows
// only its live badge (live implies test was the predecessor). Prevents
// visual clutter and stops "Published (test)" filter from double-counting
// already-promoted articles.
type PublishBadge = ArticleWithContext['publish_targets'][number];
function effectivePublishTargets(targets: PublishBadge[] | undefined): PublishBadge[] {
  if (!targets || targets.length === 0) return [];
  const byTarget = new Map<string, { test?: PublishBadge; live?: PublishBadge; other: PublishBadge[] }>();
  for (const t of targets) {
    const bucket = byTarget.get(t.target_name) ?? { other: [] };
    if (t.destination_type === 'main_branch') bucket.live = t;
    else if (t.destination_type === 'test_branch') bucket.test = t;
    else bucket.other.push(t);
    byTarget.set(t.target_name, bucket);
  }
  const out: PublishBadge[] = [];
  byTarget.forEach(b => {
    if (b.live) out.push(b.live);
    else if (b.test) out.push(b.test);
    out.push(...b.other);
  });
  return out;
}

function buildArticleJobMap(jobs: DashboardJob[] | undefined): Map<string, ArticleJobInfo> {
  const map = new Map<string, ArticleJobInfo>();
  if (!jobs) return map;
  for (const j of jobs) {
    const articleId = j.payload?.article_id as string | undefined;
    if (!articleId) continue;
    const label = JOB_LABELS[j.job_type];
    if (!label) continue;
    const isRunning = j.status === 'running' || j.status === 'retrying';
    map.set(articleId, {
      job_type: j.job_type,
      status: j.status,
      label: isRunning ? label.running : label.pending,
      tone: label.tone,
    });
  }
  return map;
}

function groupByEntity(items: AnnouncedArticle[]): EntityGroup[] {
  const groups = new Map<string, EntityGroup>();

  for (const item of items) {
    // Group by the article's own entity, not by the anchor announcement's
    // company_code. Entity-scoped synthesis (spike_explainer) attributes to
    // the spiking ticker via article.entity_canonical_identifier but its
    // announcement_id FK points at an arbitrary anchor (e.g. another ticker's
    // filing), so announcement.company_code would cluster it under the wrong
    // company. Fall back to announcement.company_code only when the article
    // has no resolved entity.
    const articleCode = item.article.entity_canonical_identifier || item.announcement.company_code;
    const isResearch = ['PRIVATE', 'OTC', 'OTCQX', 'OTCQB', 'PINK', 'PENDING'].includes(articleCode);
    // PENDING = direct-input research with no issuer (2026-08-03): its
    // company_name is the "Extracting metadata..." placeholder — never show
    // it; label the group as Research and key it per announcement so two
    // unrelated research docs don't merge.
    const isPendingResearch = articleCode === 'PENDING';
    const entityKey = isPendingResearch
      ? `research-${item.announcement.announcement_id}`
      : isResearch
        ? `research-${item.article.entity_display_name || item.announcement.company_name}`
        : articleCode;
    const entityLabel = isPendingResearch
      ? 'RESEARCH'
      : isResearch
        ? (item.article.entity_display_name || item.announcement.company_name)
        : articleCode;
    // Prefer entity_display_name from the entity system when available
    const companyName = isPendingResearch
      ? 'Research report'
      : (item.article.entity_display_name || item.announcement.company_name);

    if (!groups.has(entityKey)) {
      groups.set(entityKey, {
        entityKey,
        entityLabel,
        companyName,
        latestDate: item.article.generation_timestamp,
        articles: [],
      });
    }

    const group = groups.get(entityKey)!;
    group.articles.push(item);
    if (item.article.generation_timestamp > group.latestDate) {
      group.latestDate = item.article.generation_timestamp;
    }
    // Update company name if a better entity name becomes available
    if (item.article.entity_display_name && group.companyName !== item.article.entity_display_name) {
      group.companyName = item.article.entity_display_name;
    }
  }

  // Sort groups by latest article date (newest first)
  return Array.from(groups.values()).sort(
    (a, b) => new Date(b.latestDate).getTime() - new Date(a.latestDate).getTime()
  );
}

// B2-2 name-gate hold (2026-10-14): the pre-publish name check left this
// auto-publish a draft. Mirrors NAME_CHECK_HELD_SQL in the backend's
// database/queries.py. The draft condition matters: nothing clears the flag,
// so a released article must drop out of the queue.
function isNameCheckHeld(article: ArticleWithContext['article']): boolean {
  return article.publish_status === 'draft'
    && article.article_metadata?.auto_publish_held === 'name_mismatch';
}

export function ArticlesPage({ reviewQueue = false }: { reviewQueue?: boolean } = {}) {
  const [page, setPage] = useState(1);
  const [actionConfirm, setActionConfirm] = useState<{
    articleId: string;
    articleTitle: string;
    publishStatus: string;
    permanentDelete: boolean;
  } | null>(null);
  const [showArchived, setShowArchived] = useState(false);
  const [stateFilter, setStateFilter] = useState<'all' | 'draft' | 'approved' | 'published_test' | 'promoted_live' | 'review_blocked' | 'expired'>(reviewQueue ? 'review_blocked' : 'all');
  // P8: expired articles hide with archived; the Expired filter asks for them.
  const includeHidden = showArchived || stateFilter === 'expired';
  const [collapsedGroups, setCollapsedGroups] = useState<Set<string>>(new Set());
  const [collapsedAnnouncements, setCollapsedAnnouncements] = useState<Set<string>>(new Set());
  // Lane tab (backend services/article_lanes.py — one rule for the listing
  // and the day counts): `per_entity` (default) ↔ `sitewide` (briefs and
  // wraps, grouped by synthesis_kind) ↔ `syntheses` (story syntheses) ↔
  // `direct_input` (research/briefs). Honours an initial ?lane= from the URL so
  // the dashboard coverage-card path rows deep-link straight to the lane.
  const [searchParams] = useSearchParams();
  const _initialLane = searchParams.get('lane');
  const [lane, setLane] = useState<ArticleLane>(
    ARTICLE_LANES.includes(_initialLane as ArticleLane) ? (_initialLane as ArticleLane) : 'per_entity',
  );
  // Date strip + search — same UX as Socials/Notifications. Default lands on
  // today's articles; operator navigates back via the strip or types into
  // search to query across all days. Mutually exclusive in the request:
  // when `searchActive`, the `date` param is dropped so search spans dates.
  const [anchorDate, setAnchorDate] = useState<string>(todayYMD());
  const [selectedDate, setSelectedDate] = useState<string>(todayYMD());
  const [searchInput, setSearchInput] = useState('');
  const debouncedSearch = useDebounced(searchInput.trim(), 300);
  const searchActive = debouncedSearch.length > 0;
  // Entity-group pill (sticky across all four list pages via localStorage).
  const [entityGroup] = useEntityGroupPill();
  // 'all' = no filter — drop the param so the backend treats it as omitted.
  const entityGroupParam = entityGroup === 'all' ? undefined : entityGroup;
  // When filtering by single day (or actively searching) we ask the backend
  // for a wide page so the operator can see the whole day's output without
  // clicking through pages. 200 mirrors Socials/Notifications.
  const limit = 200;
  const [selectedArticles, setSelectedArticles] = useState<Set<string>>(new Set());
  const queryClient = useQueryClient();

  const days = useMemo(() => {
    const out: string[] = [];
    for (let i = 6; i >= 0; i--) out.push(shiftDays(anchorDate, -i));
    return out;
  }, [anchorDate]);

  // Search resets pagination; same for day picks.
  const resetPage = useCallback(() => setPage(1), []);

  const toggleSelection = useCallback((articleId: string) => {
    setSelectedArticles(prev => {
      const next = new Set(prev);
      if (next.has(articleId)) next.delete(articleId);
      else next.add(articleId);
      return next;
    });
  }, []);

  // allSettled so one failure (e.g. an article already approved, returning
  // 409) doesn't abort the whole batch. The backend's /approve endpoint only
  // accepts publish_status='draft'; anything else comes back as a 409 and
  // we surface a summary alert rather than silently swallowing it.
  const bulkApproveMutation = useMutation({
    mutationFn: async (ids: string[]) => {
      const results = await Promise.allSettled(ids.map(id => approveArticle(id)));
      const failures = results
        .map((r, idx) => ({ r, id: ids[idx] }))
        .filter(({ r }) => r.status === 'rejected');
      return { approved: ids.length - failures.length, failures };
    },
    onSuccess: ({ approved, failures }) => {
      queryClient.invalidateQueries({ queryKey: ['articles-gallery'] });
      setSelectedArticles(new Set());
      if (failures.length > 0) {
        const sample = failures.slice(0, 3).map(f => (f.r as PromiseRejectedResult).reason?.message || 'Unknown error').join('\n');
        alert(`Approved ${approved} article(s). ${failures.length} failed (likely already approved/published):\n${sample}${failures.length > 3 ? '\n…' : ''}`);
      }
    },
  });

  const bulkArchiveMutation = useMutation({
    mutationFn: async (ids: string[]) => {
      await Promise.all(ids.map(id => archiveArticle(id)));
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['articles-gallery'] });
      setSelectedArticles(new Set());
    },
  });

  // P4 (review-queue retro, operator-approved 2026-10-06): ONE decision for
  // every held masthead copy of the same filing (~33% of holds are copies).
  // Each call is the existing per-article endpoint, so audit and permissions
  // are unchanged; a release carries the same operator reason on every copy.
  const [groupOverride, setGroupOverride] = useState<{ ids: string[]; metadata: any } | null>(null);
  const groupDecision = useMutation({
    mutationFn: async ({ action, ids, reason }: { action: 'rejudge' | 'release' | 'archive'; ids: string[]; reason?: string }) => {
      const results = await Promise.allSettled(ids.map((id) =>
        action === 'rejudge' ? rejudgeArticle(id)
          : action === 'release' ? approveArticle(id, { override: true, override_reason: reason })
            : archiveArticle(id)));
      const failures = results.filter((r) => r.status === 'rejected') as PromiseRejectedResult[];
      return { action, done: ids.length - failures.length, failures };
    },
    onSuccess: ({ action, done, failures }) => {
      queryClient.invalidateQueries({ queryKey: ['articles-gallery'] });
      if (failures.length > 0) {
        alert(`${action}: ${done} done, ${failures.length} failed:\n${failures.slice(0, 3).map((f) => f.reason?.message || 'Unknown error').join('\n')}`);
      }
    },
  });

  // Bulk unpublish: for each selected article, queue an unpublish job for
  // every target it's currently published to. Uses destination_name from the
  // publish_targets badge so the backend hits the right destination (not its
  // 'test' default). Articles with no publish_targets are skipped silently.
  const bulkUnpublishMutation = useMutation({
    mutationFn: async (selection: Array<{ article_id: string; publish_targets: Array<{ target_name: string; destination_name?: string | null }> }>) => {
      const calls: Promise<unknown>[] = [];
      for (const sel of selection) {
        for (const pt of sel.publish_targets) {
          calls.push(
            unpublishArticle(sel.article_id, {
              target_name: pt.target_name,
              destination_name: pt.destination_name ?? undefined,
            }).catch(err => {
              // Don't let one failure stop the batch — surface later via query invalidation
              console.warn('unpublish failed', sel.article_id, pt.target_name, err);
            }),
          );
        }
      }
      await Promise.all(calls);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['articles-gallery'] });
      setSelectedArticles(new Set());
    },
  });

  // Bulk delete: permanent. Backend's DELETE /articles/:id refuses to delete
  // published articles, so this only works on drafts/archived. We collect
  // errors and show a single summary rather than spamming alerts per failure.
  const bulkDeleteMutation = useMutation({
    mutationFn: async (ids: string[]) => {
      const results = await Promise.allSettled(ids.map(id => deleteArticle(id)));
      const failures = results
        .map((r, idx) => ({ r, id: ids[idx] }))
        .filter(({ r }) => r.status === 'rejected');
      return { deleted: ids.length - failures.length, failures };
    },
    onSuccess: ({ deleted, failures }) => {
      queryClient.invalidateQueries({ queryKey: ['articles-gallery'] });
      setSelectedArticles(new Set());
      if (failures.length > 0) {
        const sample = failures.slice(0, 3).map(f => (f.r as PromiseRejectedResult).reason?.message || 'Unknown error').join('\n');
        alert(`Deleted ${deleted} article(s). ${failures.length} failed (likely still published):\n${sample}${failures.length > 3 ? '\n…' : ''}`);
      }
    },
  });

  // Bulk publish: for each approved article with an intended target, queue a
  // publish job. Uses the intended_target_name / intended_destination_name
  // already joined onto the article record (via content_products) — no modal.
  // Articles without an intended target are skipped silently.
  const bulkPublishMutation = useMutation({
    mutationFn: async (selection: Array<{ article_id: string; target_name: string; destination_name?: string }>) => {
      const results = await Promise.allSettled(
        selection.map(sel =>
          publishArticle(sel.article_id, {
            target_name: sel.target_name,
            destination_name: sel.destination_name,
          }),
        ),
      );
      const failures = results
        .map((r, idx) => ({ r, sel: selection[idx] }))
        .filter(({ r }) => r.status === 'rejected');
      return { queued: selection.length - failures.length, failures };
    },
    onSuccess: ({ queued, failures }) => {
      queryClient.invalidateQueries({ queryKey: ['articles-gallery'] });
      setSelectedArticles(new Set());
      if (failures.length > 0) {
        const sample = failures.slice(0, 3).map(f => (f.r as PromiseRejectedResult).reason?.message || 'Unknown error').join('\n');
        alert(`Queued ${queued} publish job(s). ${failures.length} failed:\n${sample}${failures.length > 3 ? '\n…' : ''}`);
      }
    },
  });

  // Bulk promote: for each selected article already published to a target,
  // open a PR promoting test_branch → main_branch on that target. Same
  // per-target fan-out pattern as bulk unpublish.
  const bulkPromoteMutation = useMutation({
    mutationFn: async (selection: Array<{ article_id: string; target_names: string[] }>) => {
      const tasks: Array<{ article_id: string; target_name: string; p: Promise<unknown> }> = [];
      for (const sel of selection) {
        for (const tn of sel.target_names) {
          tasks.push({
            article_id: sel.article_id,
            target_name: tn,
            p: promoteArticle(sel.article_id, tn),
          });
        }
      }
      const results = await Promise.allSettled(tasks.map(t => t.p));
      const failures = results
        .map((r, idx) => ({ r, task: tasks[idx] }))
        .filter(({ r }) => r.status === 'rejected');
      return { promoted: tasks.length - failures.length, failures };
    },
    onSuccess: ({ promoted, failures }) => {
      queryClient.invalidateQueries({ queryKey: ['articles-gallery'] });
      setSelectedArticles(new Set());
      if (failures.length > 0) {
        const sample = failures.slice(0, 3).map(f => (f.r as PromiseRejectedResult).reason?.message || 'Unknown error').join('\n');
        alert(`Created ${promoted} promotion PR(s). ${failures.length} failed:\n${sample}${failures.length > 3 ? '\n…' : ''}`);
      } else {
        alert(`Created ${promoted} promotion PR(s).`);
      }
    },
  });

  const navigate = useNavigate();

  const toggleGroup = useCallback((entityKey: string) => {
    setCollapsedGroups(prev => {
      const next = new Set(prev);
      if (next.has(entityKey)) {
        next.delete(entityKey);
      } else {
        next.add(entityKey);
      }
      return next;
    });
  }, []);

  const toggleAnnouncement = useCallback((announcementId: string) => {
    setCollapsedAnnouncements(prev => {
      const next = new Set(prev);
      if (next.has(announcementId)) {
        next.delete(announcementId);
      } else {
        next.add(announcementId);
      }
      return next;
    });
  }, []);

  const { data, isLoading, error } = useQuery({
    queryKey: searchActive
      ? ['articles-gallery', 'search', debouncedSearch, lane, includeHidden, entityGroup]
      : ['articles-gallery', 'date', selectedDate, lane, includeHidden, page, entityGroup],
    queryFn: () =>
      getAnnouncementsWithArticles(
        searchActive
          ? { page: 1, limit, include_archived: includeHidden, lane, q: debouncedSearch, entity_group: entityGroupParam }
          : { page, limit, include_archived: includeHidden, lane, date: selectedDate, entity_group: entityGroupParam },
      ),
    retry: false,
  });

  // Live job status: reuse the dashboard query (shared cache key with
  // DashboardPage) + subscribe to publish/unpublish/promote WS events so we
  // invalidate promptly instead of waiting for the polling interval. 5s
  // polling is the safety net; WS gives near-instant updates.
  const invalidateJobs = useCallback(() => {
    queryClient.invalidateQueries({ queryKey: ['dashboard'] });
  }, [queryClient]);

  const invalidateJobsAndGallery = useCallback(() => {
    queryClient.invalidateQueries({ queryKey: ['dashboard'] });
    queryClient.invalidateQueries({ queryKey: ['articles-gallery'] });
  }, [queryClient]);

  useWebSocket({
    channels: ['assayerai:announcements', 'assayerai:articles'],
    onEvent: {
      publish_started: invalidateJobs,
      publish_completed: invalidateJobsAndGallery,
      publish_failed: invalidateJobsAndGallery,
      unpublish_started: invalidateJobs,
      unpublish_completed: invalidateJobsAndGallery,
      unpublish_failed: invalidateJobsAndGallery,
      promote_started: invalidateJobs,
      promote_completed: invalidateJobsAndGallery,
      promote_failed: invalidateJobsAndGallery,
      // article_status_changed fires on approve / override / archive
      // mutations and on the QG-flips-to-review_blocked path. Refresh
      // the articles gallery so badges (review_blocked, approved,
      // archived) reflect the new state for everyone watching.
      article_status_changed: invalidateJobsAndGallery,
    },
  });

  // 10 s, not 5: the dashboard is shared and cached server-side for 10 s
  // (2026-10-06), so a faster poll only re-reads the same answer.
  const { data: dashboardData } = useQuery(dashboardQueryOptions(10_000));

  // Per-date counts for the day-strip badges. Honours the strip's filters
  // (entity_group + status). Review-queue mode counts held articles; the
  // normal Articles page counts all articles that day. Skipped during search
  // (the strip is hidden then).
  // Split by lane (the gallery's own rule), so each tab shows its count for
  // the selected day and the tab counts sum to the day badge.
  const { data: laneDateCounts } = useQuery({
    queryKey: ['article-date-counts', 'by-lane', days[0], days[days.length - 1], reviewQueue, entityGroupParam, includeHidden],
    queryFn: () =>
      getArticleLaneDateCounts({
        from: days[0],
        to: days[days.length - 1],
        status: reviewQueue ? 'review_blocked' : undefined,
        entity_group: entityGroupParam,
        include_archived: includeHidden,
      }),
    enabled: !searchActive,
    retry: false,
  });
  const dateCounts = useMemo(() => {
    if (!laneDateCounts) return undefined;
    const out: Record<string, number> = {};
    for (const [d, c] of Object.entries(laneDateCounts)) out[d] = c.total;
    return out;
  }, [laneDateCounts]);
  // Day-scoped, so hidden while searching (search spans every day).
  const selectedDayLaneCounts = searchActive ? undefined : laneDateCounts?.[selectedDate];

  const articleJobs = useMemo(
    () => buildArticleJobMap(dashboardData?.active_jobs),
    [dashboardData?.active_jobs],
  );

  // Fetch active story threads for entity indicators
  const { data: activeThreadsData } = useQuery({
    queryKey: ['active-entity-threads'],
    queryFn: async () => {
      const res = await apiClient.get('/admin/threads', {
        params: { status: 'detected', thread_type: 'entity', limit: 100 },
      });
      return res.data;
    },
  });

  // Map thread_key (company_code) → thread for quick lookup
  const entityThreadMap = useMemo(() => {
    const map = new Map<string, { thread_id: string; display_name: string; member_count: number }>();
    if (activeThreadsData?.items) {
      for (const t of activeThreadsData.items) {
        map.set(t.thread_key, {
          thread_id: t.thread_id,
          display_name: t.display_name,
          member_count: t.member_count,
        });
      }
    }
    return map;
  }, [activeThreadsData]);

  const filteredItems = useMemo(() => {
    if (!data?.items) return [] as ArticleWithContext[];
    if (stateFilter === 'all') return data.items;
    return data.items.filter((it) => {
      const status = it.article.publish_status;
      const effective = effectivePublishTargets(it.publish_targets);
      const hasTest = effective.some(t => t.destination_type === 'test_branch');
      const hasLive = effective.some(t => t.destination_type === 'main_branch');
      switch (stateFilter) {
        case 'draft': return status === 'draft';
        case 'approved': return status === 'approved';
        case 'review_blocked': return status === 'review_blocked' || isNameCheckHeld(it.article);
        case 'expired': return status === 'expired';
        case 'published_test': return hasTest;
        case 'promoted_live': return hasLive;
        default: return true;
      }
    });
  }, [data?.items, stateFilter]);

  // Direct-input lane (operator direction 2026-08-03): user-initiated
  // content — research reports, briefs — is not "an announcement linked to
  // a specific entity" and gets its own tab instead of masquerading as an
  // entity group (the PENDING "Extracting metadata..." panel).
  // The server lanes the payload (backend 0.3.118), so these only narrow
  // the type: per-entity and direct-input items always carry an announcement.
  const directInputItems = useMemo(
    () => (lane === 'direct_input' ? filteredItems.filter(hasAnnouncement) : []),
    [filteredItems, lane],
  );
  const perEntityItems = useMemo(
    () => (lane === 'per_entity' ? filteredItems.filter(hasAnnouncement) : []),
    [filteredItems, lane],
  );
  // Group direct-input articles by their source document.
  const directInputGroups = useMemo(() => {
    const map = new Map<string, EntityGroup>();
    for (const it of directInputItems) {
      const key = it.announcement.announcement_id;
      const label = it.announcement.source_type === 'editorial_brief' ? 'BRIEF' : 'RESEARCH';
      if (!map.has(key)) {
        map.set(key, {
          entityKey: `direct-${key}`,
          entityLabel: label,
          companyName: it.announcement.announcement_title || 'Submitted document',
          latestDate: it.article.generation_timestamp,
          articles: [],
        });
      }
      const g = map.get(key)!;
      g.articles.push(it);
      if (it.article.generation_timestamp > g.latestDate) g.latestDate = it.article.generation_timestamp;
    }
    return Array.from(map.values()).sort((a, b) => new Date(b.latestDate).getTime() - new Date(a.latestDate).getTime());
  }, [directInputItems]);

  const entityGroups = useMemo(() => groupByEntity(perEntityItems), [perEntityItems]);

  // Site-wide grouping: bucket synthesis articles by synthesis_kind so the
  // Site-wide tab reads as a per-lane summary (Pre-Market / Market-Open /
  // Close Wrap / Sector Wrap / Weekly Theme / Director-Trade Roundup),
  // independent of which arbitrary anchor announcement they're FK'd to.
  const synthesisGroups = useMemo(() => {
    const KIND_LABELS: Record<string, string> = {
      pre_market_brief: 'Pre-Market Brief',
      market_open_report: 'Market-Open Report',
      close_wrap: 'Close Wrap',
      sector_wrap: 'Sector Wrap',
      weekly_theme: 'Weekly Theme',
      overnight_wrap: 'Overnight Wrap',
      story_synthesis: 'Story syntheses',
      director_trade_roundup: 'Director-Trade Roundup',
      activity: 'Activity Roundup',
      sector: 'Sector Intelligence',
      macro: 'Macro Analysis',
      opinion: 'Opinion',
    };
    const KIND_ORDER = [
      'pre_market_brief', 'market_open_report', 'close_wrap',
      'sector_wrap', 'overnight_wrap', 'weekly_theme', 'story_synthesis', 'director_trade_roundup',
      'activity', 'sector', 'macro', 'opinion',
    ];
    const buckets = new Map<string, ArticleWithContext[]>();
    for (const item of filteredItems) {
      const kind = item.article.synthesis_kind ?? 'other';
      if (!buckets.has(kind)) buckets.set(kind, []);
      buckets.get(kind)!.push(item);
    }
    const out: { kind: string; label: string; articles: ArticleWithContext[] }[] = [];
    for (const kind of KIND_ORDER) {
      const arr = buckets.get(kind);
      if (arr && arr.length) {
        out.push({ kind, label: KIND_LABELS[kind] ?? kind, articles: arr });
      }
    }
    // Any unrecognised kinds fall through at the bottom.
    Array.from(buckets.entries()).forEach(([kind, arr]) => {
      if (!KIND_ORDER.includes(kind) && arr.length) {
        out.push({ kind, label: KIND_LABELS[kind] ?? kind, articles: arr });
      }
    });
    // Within each bucket, newest-first.
    for (const g of out) {
      g.articles.sort(
        (a, b) => new Date(b.article.generation_timestamp).getTime() -
                 new Date(a.article.generation_timestamp).getTime(),
      );
    }
    return out;
  }, [filteredItems]);

  const deleteMutation = useMutation({
    mutationFn: (articleId: string) => deleteArticle(articleId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['articles-gallery'] });
      setActionConfirm(null);
    },
    onError: (error: any) => {
      alert(`Failed to delete article: ${error.message || 'Unknown error'}`);
    },
  });

  const archiveMutation = useMutation({
    mutationFn: (articleId: string) => archiveArticle(articleId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['articles-gallery'] });
      setActionConfirm(null);
    },
    onError: (error: any) => {
      alert(`Failed to archive article: ${error.message || 'Unknown error'}`);
    },
  });

  const handleArchiveClick = (e: React.MouseEvent, article: any) => {
    e.preventDefault();
    e.stopPropagation();
    setActionConfirm({
      articleId: article.article_id,
      articleTitle: article.article_title,
      publishStatus: article.publish_status,
      permanentDelete: false,
    });
  };

  // Per-site article totals sourced from the dashboard endpoint so the figure
  // spans the whole corpus, not just the current page-of-50. Three counts:
  //   - generated today: articles created today (incl. drafts + synthesis)
  //   - live today:      articles that shipped to main_branch today
  //   - overall:         total non-archived articles ever
  // Separating "generated" from "live" makes the gap between pipeline output
  // and shipped content visible — a chip reading "4 generated · 1 live" tells
  // the operator only one of today's four pieces actually made it to readers.
  const articlesBySite = dashboardData?.articles_by_site ?? [];
  const articlesGeneratedToday = articlesBySite.reduce((n, r) => n + r.generated_today, 0);
  const articlesLiveToday = articlesBySite.reduce((n, r) => n + r.live_today, 0);
  const articlesTotalOverall = articlesBySite.reduce((n, r) => n + r.overall, 0);

  // Tone classes mirror the per-article content_product_name badge (orecast =
  // amber, bellwire = sky) so the summary chips read the same as the rows.
  const siteToneClass = (targetName: string): string => {
    const t = targetName.toLowerCase();
    if (t === 'orecast') return 'bg-amber-100 text-amber-700';
    if (t === 'bellwire') return 'bg-sky-100 text-sky-700';
    if (t === 'marketfloor') return 'bg-emerald-100 text-emerald-700';
    return 'bg-grey-100 text-grey-700';
  };

  // egress_targets only stores the slug (target_name); friendly labels
  // map here. Unknown targets fall through to a Title-cased version of
  // the slug so a new target shows up legibly without a code change.
  const siteDisplayName = (targetName: string): string => {
    const t = targetName.toLowerCase();
    if (t === 'orecast') return 'Orecast';
    if (t === 'bellwire') return 'Bell Wire';
    if (t === 'marketfloor') return 'Market Floor';
    if (t === 'unrouted') return 'Unrouted';
    return targetName.charAt(0).toUpperCase() + targetName.slice(1);
  };

  return (
    <div className="space-y-6 max-w-7xl mx-auto px-4 py-6">
      {/* Filter bar */}
      <div className="flex items-center justify-between flex-wrap gap-3">
        <h1 className="text-2xl font-bold text-grey-900">{reviewQueue ? 'Review Queue' : 'Articles'}</h1>
        <div className="flex items-center gap-4">
          <label className="flex items-center gap-2 text-sm text-grey-600">
            <span>State</span>
            <select
              value={stateFilter}
              onChange={(e) => setStateFilter(e.target.value as typeof stateFilter)}
              className="h-8 px-2 rounded border border-grey-300 bg-surface text-sm text-grey-800 focus:border-primary-500 focus:outline-none"
            >
              <option value="all">All</option>
              <option value="draft">Draft</option>
              <option value="review_blocked">Held for review</option>
              <option value="expired">Expired (unreviewed)</option>
              <option value="approved">Approved</option>
              <option value="published_test">Published (test)</option>
              <option value="promoted_live">Promoted (live)</option>
            </select>
          </label>
          <label className="flex items-center space-x-2 text-sm text-grey-600 cursor-pointer">
            <input
              type="checkbox"
              checked={showArchived}
              onChange={(e) => { setShowArchived(e.target.checked); setPage(1); }}
              className="h-4 w-4 rounded border-grey-300 text-primary-600 focus:ring-primary-500"
            />
            <span>Show archived</span>
          </label>
        </div>
      </div>

      {/* Entity-group filter pills — sticky across Articles, Announcements,
          Notifications, Social. Narrows ALL queries on this page by entity
          membership before the per-page date/search filters apply. */}
      <EntityGroupFilter />

      {/* Search — full-width row above the day strip. Mirrors Socials /
          Notifications: typing here spans all dates and statuses, the day
          strip is hidden while search is active. */}
      <div className="flex items-center gap-2">
        <div className="relative flex-1 max-w-md">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-grey-400" />
          <input
            type="search"
            value={searchInput}
            onChange={(e) => { setSearchInput(e.target.value); resetPage(); }}
            placeholder="Search by title, ticker, company, or announcement…"
            className="w-full pl-9 pr-9 py-2 text-sm border border-grey-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-200 focus:border-primary-400"
          />
          {searchInput && (
            <button
              onClick={() => setSearchInput('')}
              className="absolute right-2 top-1/2 -translate-y-1/2 text-grey-400 hover:text-grey-700"
              aria-label="Clear search"
            >
              <XIcon className="h-4 w-4" />
            </button>
          )}
        </div>
        {searchActive && (
          <span className="text-xs text-grey-500">Searching across all dates</span>
        )}
      </div>

      {/* Day strip — hidden when search is active (search spans all days). */}
      {!searchActive && (
        <DateBar
          days={days}
          selectedDate={selectedDate}
          onSelect={(d) => { setSelectedDate(d); resetPage(); }}
          onShiftWeek={(delta) => setAnchorDate(shiftDays(anchorDate, delta * 7))}
          onToday={() => {
            setAnchorDate(todayYMD());
            setSelectedDate(todayYMD());
            resetPage();
          }}
          counts={dateCounts}
        />
      )}

      {/* Lane tabs, one per lane of the backend's lane rule. Each shows its
          count for the selected day; the counts sum to the day badge, and
          the line under the tabs spells that out (operator 2026-10-12). */}
      <div className="border-b border-grey-200">
        <nav className="-mb-px flex gap-6 overflow-x-auto" aria-label="Article lane">
          {ARTICLE_LANES.map((l) => (
            <button
              key={l}
              onClick={() => { setLane(l); setPage(1); }}
              className={`py-2 px-1 border-b-2 font-medium text-sm whitespace-nowrap transition-colors ${
                lane === l
                  ? 'border-primary-500 text-primary-600'
                  : 'border-transparent text-grey-500 hover:text-grey-700 hover:border-grey-300'
              }`}
            >
              {LANE_LABELS[l]}
              {selectedDayLaneCounts && (
                <span className={`ml-1.5 px-1.5 py-0.5 text-[10px] font-bold rounded ${
                  selectedDayLaneCounts[l] > 0 ? 'bg-grey-200 text-grey-700' : 'bg-grey-100 text-grey-400'
                }`}>
                  {selectedDayLaneCounts[l]}
                </span>
              )}
            </button>
          ))}
        </nav>
      </div>
      {selectedDayLaneCounts && (
        <p className="text-xs text-grey-500 -mt-2">
          {format(new Date(`${selectedDate}T00:00:00`), 'EEE d MMM')}
          {reviewQueue ? ' held' : ''}: <span className="font-semibold text-grey-700">{selectedDayLaneCounts.total}</span>
          {' = '}
          {ARTICLE_LANES.map((l, i) => (
            <span key={l}>{i > 0 ? ' + ' : ''}{selectedDayLaneCounts[l]} {LANE_LABELS[l].toLowerCase()}</span>
          ))}
        </p>
      )}

      {/* Per-site summary — corpus-wide counts sourced from the shared
          dashboard query rather than the paginated page data. "Generated"
          counts everything created today (drafts + synthesis included);
          "live" counts what successfully shipped to main_branch today. */}
      {articlesBySite.length > 0 && (
        <div className="flex items-center gap-3 flex-wrap text-sm">
          <span className="text-grey-500 font-medium">
            <span className="text-grey-900 font-semibold">{articlesGeneratedToday}</span> generated
            <span className="text-grey-400"> · </span>
            <span className="text-grey-900 font-semibold">{articlesLiveToday}</span> live
            <span className="text-grey-400"> · </span>
            <span className="text-grey-900 font-semibold">{articlesTotalOverall}</span> total
          </span>
          <span className="text-grey-300">·</span>
          {articlesBySite.map((row) => {
            const label = siteDisplayName(row.target_name);
            return (
              <span
                key={row.target_name}
                className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium ${siteToneClass(row.target_name)}`}
                title={`${label}: ${row.generated_today} generated today · ${row.live_today} live today · ${row.overall} total`}
              >
                <span>{label}</span>
                <span className="font-semibold">{row.generated_today}</span>
                <span className="opacity-60">gen</span>
                <span className="opacity-40">·</span>
                <span className="font-semibold">{row.live_today}</span>
                <span className="opacity-60">live</span>
                <span className="opacity-40">·</span>
                <span className="font-semibold">{row.overall}</span>
                <span className="opacity-60">total</span>
              </span>
            );
          })}
        </div>
      )}

      {/* Bulk Action Bar */}
      {selectedArticles.size > 0 && (() => {
        // Compute selected articles that actually have publish_targets so the
        // Unpublish button can be enabled/disabled correctly and we build the
        // right payload for the mutation.
        const flatItems = (data?.items ?? []) as ArticleWithContext[];
        const selectedItems = flatItems.filter(it => selectedArticles.has(it.article.article_id));

        const selectedPublishable = selectedItems
          .filter(it => (it.publish_targets?.length ?? 0) > 0)
          .map(it => ({
            article_id: it.article.article_id,
            publish_targets: it.publish_targets.map(pt => ({
              target_name: pt.target_name,
              destination_name: pt.destination_name,
            })),
          }));
        const unpublishCount = selectedPublishable.length;

        // Bulk publish: eligible = approved articles with an intended target
        // derived from content_products routing (joined on by the backend).
        const selectedApprovedWithIntent = selectedItems
          .filter(it => it.article.publish_status === 'approved')
          .map(it => {
            const a = it.article as any;
            return {
              article_id: it.article.article_id,
              target_name: a.intended_target_name as string | null | undefined,
              destination_name: a.intended_destination_name as string | null | undefined,
            };
          })
          .filter(s => !!s.target_name) as Array<{ article_id: string; target_name: string; destination_name?: string }>;
        const publishCount = selectedApprovedWithIntent.length;

        // Bulk promote: eligible = already published articles, promoted per target.
        const selectedPromotable = selectedItems
          .filter(it => it.article.publish_status === 'published' && (it.publish_targets?.length ?? 0) > 0)
          .map(it => ({
            article_id: it.article.article_id,
            target_names: Array.from(new Set(it.publish_targets.map(pt => pt.target_name))),
          }));
        const promoteCount = selectedPromotable.length;
        const promoteTargetCount = selectedPromotable.reduce((n, s) => n + s.target_names.length, 0);

        return (
          <div className="flex items-center gap-3 px-4 py-3 bg-primary-50 border border-primary-200 rounded-lg flex-wrap">
            <span className="text-sm font-medium text-primary-700">{selectedArticles.size} selected</span>
            <button
              onClick={() => bulkApproveMutation.mutate(Array.from(selectedArticles))}
              disabled={bulkApproveMutation.isPending}
              className="flex items-center gap-1.5 px-3 py-2.5 sm:py-1.5 text-xs font-medium bg-success-50 text-success-700 rounded-lg hover:bg-success-100"
            >
              <CheckCircle2 className="h-3.5 w-3.5" /> Approve
            </button>
            <button
              onClick={() => {
                if (publishCount === 0) return;
                if (!confirm(`Publish ${publishCount} approved article${publishCount === 1 ? '' : 's'} to its intended target?`)) return;
                bulkPublishMutation.mutate(selectedApprovedWithIntent);
              }}
              disabled={bulkPublishMutation.isPending || publishCount === 0}
              className="flex items-center gap-1.5 px-3 py-2.5 sm:py-1.5 text-xs font-medium bg-primary-100 text-primary-700 rounded-lg hover:bg-primary-200 disabled:opacity-50 disabled:cursor-not-allowed"
              title={publishCount === 0 ? 'No selected article is approved with a routed target' : `Publish ${publishCount} article(s) to their intended targets`}
            >
              <Send className="h-3.5 w-3.5" /> Publish{publishCount > 0 ? ` (${publishCount})` : ''}
            </button>
            <button
              onClick={() => {
                if (promoteCount === 0) return;
                if (!confirm(`Open ${promoteTargetCount} promotion PR(s) for ${promoteCount} published article${promoteCount === 1 ? '' : 's'} (test → main)?`)) return;
                bulkPromoteMutation.mutate(selectedPromotable);
              }}
              disabled={bulkPromoteMutation.isPending || promoteCount === 0}
              className="flex items-center gap-1.5 px-3 py-2.5 sm:py-1.5 text-xs font-medium bg-success-50 text-success-700 rounded-lg hover:bg-success-100 disabled:opacity-50 disabled:cursor-not-allowed"
              title={promoteCount === 0 ? 'No selected article is currently published to test' : `Promote ${promoteCount} article(s) across ${promoteTargetCount} target(s)`}
            >
              <GitPullRequest className="h-3.5 w-3.5" /> Promote{promoteCount > 0 ? ` (${promoteCount})` : ''}
            </button>
            <button
              onClick={() => {
                if (unpublishCount === 0) return;
                if (!confirm(`Unpublish ${unpublishCount} article${unpublishCount === 1 ? '' : 's'} from ${selectedPublishable.reduce((n, s) => n + s.publish_targets.length, 0)} target(s)?`)) return;
                bulkUnpublishMutation.mutate(selectedPublishable);
              }}
              disabled={bulkUnpublishMutation.isPending || unpublishCount === 0}
              className="flex items-center gap-1.5 px-3 py-2.5 sm:py-1.5 text-xs font-medium bg-danger-50 text-danger-700 rounded-lg hover:bg-danger-100 disabled:opacity-50 disabled:cursor-not-allowed"
              title={unpublishCount === 0 ? 'No selected article is currently published' : `Unpublish ${unpublishCount} article(s)`}
            >
              <Undo2 className="h-3.5 w-3.5" /> Unpublish{unpublishCount > 0 ? ` (${unpublishCount})` : ''}
            </button>
            <button
              onClick={() => bulkArchiveMutation.mutate(Array.from(selectedArticles))}
              disabled={bulkArchiveMutation.isPending}
              className="flex items-center gap-1.5 px-3 py-2.5 sm:py-1.5 text-xs font-medium bg-warning-50 text-warning-700 rounded-lg hover:bg-warning-100"
            >
              <Archive className="h-3.5 w-3.5" /> Archive
            </button>
            <button
              onClick={() => {
                const n = selectedArticles.size;
                if (n === 0) return;
                if (!confirm(`Permanently delete ${n} article${n === 1 ? '' : 's'}?\n\nThis cannot be undone. Published articles will be skipped and must be unpublished first.`)) return;
                bulkDeleteMutation.mutate(Array.from(selectedArticles));
              }}
              disabled={bulkDeleteMutation.isPending}
              className="flex items-center gap-1.5 px-3 py-2.5 sm:py-1.5 text-xs font-medium bg-danger-100 text-danger-800 rounded-lg hover:bg-danger-200 border border-danger-200 disabled:opacity-50 disabled:cursor-not-allowed"
              title="Permanently delete selected articles (cannot be undone)"
            >
              <Trash2 className="h-3.5 w-3.5" /> Delete
            </button>
            <button
              onClick={() => setSelectedArticles(new Set())}
              className="ml-auto text-xs text-grey-500 hover:text-grey-700"
            >
              Clear selection
            </button>
          </div>
        );
      })()}

      {isLoading ? (
        <div className="flex items-center justify-center min-h-[400px]">
          <div className="text-center">
            <div className="inline-block h-10 w-10 animate-spin rounded-full border-4 border-solid border-primary-500 border-r-transparent"></div>
            <p className="mt-6 text-grey-600 font-medium">Loading articles...</p>
          </div>
        </div>
      ) : error || !data || (lane === 'per_entity' ? entityGroups.length === 0 : lane === 'direct_input' ? directInputGroups.length === 0 : synthesisGroups.length === 0) ? (
        <div className="bg-surface rounded-xl border border-grey-200 p-16 text-center shadow-md">
          <Newspaper className="h-16 w-16 text-grey-300 mx-auto mb-4" />
          <h2 className="text-xl font-bold text-grey-900 mb-2">
            {searchActive
              ? 'No matches'
              : selectedDate !== todayYMD()
                ? 'Nothing on this day'
                : 'Nothing yet today'}
          </h2>
          <p className="text-base text-grey-600">
            {searchActive
              ? <>Nothing matched <strong>"{debouncedSearch}"</strong> on the {LANE_LABELS[lane]} lane.</>
              : lane === 'sitewide'
                ? 'No site-wide briefs or wraps generated on this date.'
                : lane === 'syntheses'
                ? 'No story syntheses generated on this date.'
                : selectedDate !== todayYMD()
                  ? 'No articles were generated on this date. Use the strip to navigate to another day.'
                  : 'No articles generated today yet. Articles appear here as soon as the pipeline produces them.'}
          </p>
        </div>
      ) : lane === 'sitewide' || lane === 'syntheses' ? (
        <>
          <div className="space-y-6">
            {synthesisGroups.map((group) => (
              <div key={group.kind} className="bg-surface rounded-xl border border-grey-200 overflow-hidden shadow-sm">
                <div className="bg-grey-50 border-b border-grey-200 px-6 py-3 flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <Globe className="h-5 w-5 text-grey-500" />
                    <span className="font-bold text-grey-900 text-base">{group.label}</span>
                  </div>
                  <span className="text-xs text-grey-500">
                    {group.articles.length} article{group.articles.length !== 1 ? 's' : ''}
                  </span>
                </div>
                <div className="divide-y divide-grey-100 bg-surface">
                  {group.articles.map((item) => (
                    <Link
                      key={item.article.article_id}
                      to={`/articles/${item.article.article_id}`}
                      className="flex flex-wrap items-center gap-x-4 gap-y-1.5 px-3 sm:px-6 py-4 hover:bg-grey-50 transition-colors relative"
                    >
                      <button
                        onClick={(e) => { e.preventDefault(); e.stopPropagation(); toggleSelection(item.article.article_id); }}
                        className="flex-shrink-0 -m-1.5 p-2 text-grey-300 hover:text-primary-600 active:text-primary-700"
                      >
                        {selectedArticles.has(item.article.article_id)
                          ? <CheckSquare className="h-4 w-4 text-primary-600" />
                          : <Square className="h-4 w-4" />
                        }
                      </button>
                      {item.is_new && (
                        <span className="absolute top-2 right-2 flex items-center gap-1 bg-primary-500 text-white px-2 py-0.5 rounded-full text-[10px] font-semibold">
                          <Sparkles className="h-2.5 w-2.5" />
                          NEW
                        </span>
                      )}
                      <div className="flex-1 min-w-0">
                        <h4 className="font-semibold text-grey-900 text-sm line-clamp-1">
                          {item.article.article_title}
                        </h4>
                        <div className="flex items-center gap-2 mt-1.5 flex-wrap">
                          {item.article.content_product_name && (
                            <span className={`inline-flex items-center px-2 py-0.5 rounded text-[10px] font-semibold ${
                              item.article.content_product_name.startsWith('Orecast')
                                ? 'bg-amber-100 text-amber-700'
                                : item.article.content_product_name.startsWith('Bell Wire')
                                ? 'bg-sky-100 text-sky-700'
                                : 'bg-grey-100 text-grey-600'
                            }`}>
                              {item.article.content_product_name}
                            </span>
                          )}
                          {item.article.voice_name && (
                            <span className="inline-flex items-center px-2 py-0.5 rounded text-[10px] font-semibold bg-grey-100 text-grey-600">
                              {item.article.voice_name}
                            </span>
                          )}
                          {item.article.persona_name && (
                            <span className="inline-flex items-center px-2 py-0.5 rounded text-[10px] font-semibold bg-primary-50 text-primary-700">
                              {item.article.persona_name}
                            </span>
                          )}
                          <span className="text-xs text-grey-400">{item.article.word_count} words</span>
                          <span className="text-xs text-grey-400">{format(new Date(item.article.generation_timestamp), 'MMM d, yyyy · HH:mm')}</span>
                        </div>
                      </div>
                      <div className="flex items-center gap-1.5 flex-wrap w-full pl-7 sm:w-auto sm:pl-0 sm:flex-shrink-0 sm:flex-nowrap">
                        {/* review_blocked indicator. Renders alongside any
                            publish_target badges so an article that was
                            published-then-flipped-to-review_blocked still
                            surfaces the gate state at a glance. */}
                        {item.article.publish_status === 'review_blocked' && (
                          <span
                            className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-semibold bg-warning-100 text-warning-800 border border-warning-300"
                            title="Quality Gate: NEEDS REVISION — open the article to review or override"
                          >
                            <ShieldAlert className="h-2.5 w-2.5" />
                            review blocked
                          </span>
                        )}
                        {isNameCheckHeld(item.article) && (
                          <span
                            className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-semibold bg-warning-100 text-warning-800 border border-warning-300"
                            title="Name check: the headline doesn't name the company the filing names, so auto-publish was held. Check the headline, then approve and publish as a draft."
                          >
                            <ShieldAlert className="h-2.5 w-2.5" />
                            name check held
                          </span>
                        )}
                        {item.article.publish_status === 'review_blocked' && item.article.mandated_needs_human && (
                          <span
                            className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-semibold bg-danger-100 text-danger-800 border border-danger-300"
                            title={`Mandated coverage: the QG editor cannot fix this mechanically (${item.article.mandated_needs_human.reason ?? 'a finding needs a human'}). Decide it before the coverage breach.`}
                          >
                            mandated · needs a human
                          </span>
                        )}
                        {item.article.auto_published && (
                          <span
                            className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-semibold bg-grey-200 text-grey-700"
                            title="Published automatically — no operator action"
                          >
                            auto
                          </span>
                        )}
                        {(() => { const effective = effectivePublishTargets(item.publish_targets); return effective.length > 0 ? (
                          effective.map((pt, idx) => (
                            <span
                              key={`${pt.target_name}-${pt.destination_type}-${idx}`}
                              className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-semibold ${
                                pt.destination_type === 'main_branch'
                                  ? 'bg-success-100 text-success-700'
                                  : 'bg-warning-100 text-warning-700'
                              }`}
                            >
                              <Globe className="h-2.5 w-2.5" />
                              {pt.target_name} {pt.destination_type === 'main_branch' ? 'live' : 'test'}
                            </span>
                          ))
                        ) : (
                          <span
                            className={`text-[10px] px-2 py-0.5 rounded-full font-semibold ${
                              item.article.publish_status === 'approved'
                                ? 'bg-primary-100 text-primary-700'
                                : item.article.publish_status === 'published'
                                ? 'bg-success-100 text-success-700'
                                : 'bg-grey-100 text-grey-500'
                            }`}
                          >
                            {item.article.publish_status}
                          </span>
                        ); })()}
                      </div>
                      {item.article.publish_status !== 'archived' && (
                        <button
                          onClick={(e) => handleArchiveClick(e, item.article)}
                          className="p-1.5 text-grey-300 hover:text-warning-600 hover:bg-warning-50 rounded transition-colors flex-shrink-0"
                          title="Archive article"
                        >
                          <Archive className="h-4 w-4" />
                        </button>
                      )}
                    </Link>
                  ))}
                </div>
              </div>
            ))}
          </div>
          {!searchActive && data.pages > 1 && (
            <div className="bg-surface rounded-xl border border-grey-200 px-8 py-5 flex items-center justify-between shadow-md mt-6">
              <div className="text-base text-grey-700 font-semibold">Page {page} of {data.pages}</div>
              <div className="flex space-x-3">
                <button
                  onClick={() => setPage(page - 1)}
                  disabled={page === 1}
                  className="px-5 py-2.5 border-2 border-grey-300 rounded-lg text-grey-700 font-semibold hover:bg-surface hover:border-primary-500 disabled:opacity-50 disabled:cursor-not-allowed transition-all"
                >
                  <ChevronLeft className="h-5 w-5" />
                </button>
                <button
                  onClick={() => setPage(page + 1)}
                  disabled={page === data.pages}
                  className="px-5 py-2.5 border-2 border-grey-300 rounded-lg text-grey-700 font-semibold hover:bg-surface hover:border-primary-500 disabled:opacity-50 disabled:cursor-not-allowed transition-all"
                >
                  <ChevronRight className="h-5 w-5" />
                </button>
              </div>
            </div>
          )}
        </>
      ) : (
        <>
          <div className="space-y-6">
            {/* Direct-input lane reuses the entity-group panel — groups are
                per source document with a RESEARCH/BRIEF label. */}
            {(lane === 'direct_input' ? directInputGroups : entityGroups).map((group) => (
              <div key={group.entityKey} className="bg-surface rounded-xl border border-grey-200 overflow-hidden shadow-sm">
                {/* Entity Header (clickable to toggle) */}
                <button
                  onClick={() => toggleGroup(group.entityKey)}
                  className="w-full bg-grey-50 border-b border-grey-200 px-6 py-4 flex items-center justify-between hover:bg-grey-100 transition-colors cursor-pointer text-left"
                >
                  <div className="flex items-center gap-3">
                    <Building2 className="h-5 w-5 text-grey-500" />
                    <div>
                      <span className="font-bold text-grey-900 text-lg">{group.entityLabel}</span>
                      {group.entityLabel !== group.companyName && (
                        <span className="ml-2 text-sm text-grey-500">{group.companyName}</span>
                      )}
                    </div>
                    <CoverageBadges tiers={group.articles[0]?.announcement?.coverage_tiers} />
                  </div>
                  <div className="flex items-center gap-3">
                    {entityThreadMap.has(group.entityKey) && (
                      <Link
                        to={`/threads/${entityThreadMap.get(group.entityKey)!.thread_id}`}
                        onClick={(e) => e.stopPropagation()}
                        className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-semibold bg-purple-100 text-purple-700 hover:bg-purple-200 transition-colors"
                        title={entityThreadMap.get(group.entityKey)!.display_name}
                      >
                        <GitBranch className="h-2.5 w-2.5" />
                        Thread ({entityThreadMap.get(group.entityKey)!.member_count})
                      </Link>
                    )}
                    <span className="text-xs text-grey-500">
                      {group.articles.length} article{group.articles.length !== 1 ? 's' : ''}
                    </span>
                    {collapsedGroups.has(group.entityKey) ? (
                      <ChevronDown className="h-4 w-4 text-grey-400" />
                    ) : (
                      <ChevronUp className="h-4 w-4 text-grey-400" />
                    )}
                  </div>
                </button>

                {/* Announcement sub-groups, each wrapping its article rows */}
                {!collapsedGroups.has(group.entityKey) && (() => {
                  // Bucket articles by announcement_id; sort announcements by
                  // date desc, articles within each by generation time desc.
                  // Synthesis articles (spike_explainer, sector wraps, etc.)
                  // have an arbitrary anchor announcement_id that doesn't
                  // belong to the article's entity — bucketing them with
                  // it would label the cluster with an unrelated filing.
                  // Give each synthesis article its own pseudo-bucket keyed
                  // by article_id so the cluster header shows the article's
                  // own title, not the anchor's.
                  const annMap = new Map<string, { announcement: typeof group.articles[0]['announcement']; articles: typeof group.articles }>();
                  for (const it of group.articles) {
                    const isSynthesis = it.article.is_synthesis === true;
                    const bucketKey = isSynthesis
                      ? `synthesis-${it.article.article_id}`
                      : it.announcement.announcement_id;
                    if (!annMap.has(bucketKey)) {
                      // For synthesis articles, override the cluster's
                      // announcement display fields with the article's own
                      // title/date so the sub-header reads sensibly.
                      const announcement = isSynthesis
                        ? {
                            ...it.announcement,
                            announcement_id: bucketKey,
                            announcement_title: it.article.article_title,
                            announcement_date: it.article.generation_timestamp,
                          }
                        : it.announcement;
                      annMap.set(bucketKey, { announcement, articles: [] });
                    }
                    annMap.get(bucketKey)!.articles.push(it);
                  }
                  const annGroups = Array.from(annMap.values()).sort(
                    (a, b) => new Date(b.announcement.announcement_date).getTime() - new Date(a.announcement.announcement_date).getTime(),
                  );
                  return (
                    <div className="divide-y divide-grey-100">
                      {annGroups.map((ag) => {
                        const annCollapsed = collapsedAnnouncements.has(ag.announcement.announcement_id);
                        return (
                          <div key={ag.announcement.announcement_id}>
                            {/* Announcement sub-header (clickable to toggle) */}
                            <button
                              onClick={() => toggleAnnouncement(ag.announcement.announcement_id)}
                              className="w-full bg-grey-50/60 border-b border-grey-100 px-6 py-2.5 flex items-center justify-between hover:bg-grey-100 transition-colors cursor-pointer text-left"
                            >
                              <div className="flex items-center gap-2 min-w-0">
                                <Newspaper className="h-3.5 w-3.5 text-grey-400 flex-shrink-0" />
                                <span className="text-sm font-medium text-grey-800 truncate">
                                  {ag.announcement.announcement_title}
                                </span>
                              </div>
                              <div className="flex items-center gap-3 flex-shrink-0">
                                <span className="text-xs text-grey-500">
                                  {format(new Date(ag.announcement.announcement_date), 'MMM d, yyyy')}
                                </span>
                                <span className="text-xs text-grey-500">
                                  {ag.articles.length} article{ag.articles.length !== 1 ? 's' : ''}
                                </span>
                                {annCollapsed ? (
                                  <ChevronDown className="h-3.5 w-3.5 text-grey-400" />
                                ) : (
                                  <ChevronUp className="h-3.5 w-3.5 text-grey-400" />
                                )}
                              </div>
                            </button>

                            {reviewQueue && (() => {
                              const held = ag.articles.filter((x: ArticleWithContext) => x.article.publish_status === 'review_blocked');
                              if (held.length < 2) return null;
                              const ids = held.map((x: ArticleWithContext) => x.article.article_id);
                              const busy = groupDecision.isPending;
                              const btn = 'inline-flex items-center gap-1 px-2.5 py-1 rounded border font-medium disabled:opacity-50';
                              return (
                                <div className="flex flex-wrap items-center gap-2 px-6 py-2 bg-warning-50/40 border-b border-grey-100 text-xs">
                                  <span className="font-medium text-grey-700">One decision for all {held.length} held copies:</span>
                                  <button disabled={busy} onClick={() => groupDecision.mutate({ action: 'rejudge', ids })}
                                    title="Re-run the Quality Gate on every copy; copies that pass are released"
                                    className={`${btn} border-grey-300 text-grey-700 hover:bg-grey-50`}>
                                    <RefreshCw className="h-3 w-3" /> Re-judge all
                                  </button>
                                  <button disabled={busy} onClick={() => setGroupOverride({ ids, metadata: held[0].article.quality_gate_metadata })}
                                    title="Approve every copy despite the Quality Gate verdict, with one audited reason"
                                    className={`${btn} border-warning-500 text-warning-800 hover:bg-warning-50`}>
                                    <ShieldAlert className="h-3 w-3" /> Release all (override)…
                                  </button>
                                  <button disabled={busy} onClick={() => { if (confirm(`Archive all ${held.length} copies of this filing?`)) groupDecision.mutate({ action: 'archive', ids }); }}
                                    className={`${btn} border-grey-300 text-grey-700 hover:bg-grey-50`}>
                                    <Archive className="h-3 w-3" /> Archive all
                                  </button>
                                  <span className="text-grey-500">Per-masthead differences are on each copy below.</span>
                                </div>
                              );
                            })()}

                            {!annCollapsed && (
                              <div className="divide-y divide-grey-100 bg-surface">
                                {ag.articles
                                  .sort((a, b) => new Date(b.article.generation_timestamp).getTime() - new Date(a.article.generation_timestamp).getTime())
                                  .map((item) => (
                    <Link
                      key={item.article.article_id}
                      to={`/articles/${item.article.article_id}`}
                      className="flex flex-wrap items-center gap-x-4 gap-y-1.5 px-3 sm:px-6 py-4 hover:bg-grey-50 transition-colors relative"
                    >
                      {/* Selection checkbox */}
                      <button
                        onClick={(e) => { e.preventDefault(); e.stopPropagation(); toggleSelection(item.article.article_id); }}
                        className="flex-shrink-0 -m-1.5 p-2 text-grey-300 hover:text-primary-600 active:text-primary-700"
                      >
                        {selectedArticles.has(item.article.article_id)
                          ? <CheckSquare className="h-4 w-4 text-primary-600" />
                          : <Square className="h-4 w-4" />
                        }
                      </button>

                      {/* New badge */}
                      {item.is_new && (
                        <span className="absolute top-2 right-2 flex items-center gap-1 bg-primary-500 text-white px-2 py-0.5 rounded-full text-[10px] font-semibold">
                          <Sparkles className="h-2.5 w-2.5" />
                          NEW
                        </span>
                      )}

                      {/* Article info */}
                      <div className="flex-1 min-w-0">
                        <h4 className="font-semibold text-grey-900 text-sm line-clamp-1">
                          {item.article.article_title}
                        </h4>
                        <div className="flex items-center gap-2 mt-1.5 flex-wrap">
                          {item.article.content_product_name && (
                            <span className={`inline-flex items-center px-2 py-0.5 rounded text-[10px] font-semibold ${
                              item.article.content_product_name.startsWith('Orecast')
                                ? 'bg-amber-100 text-amber-700'
                                : item.article.content_product_name.startsWith('Bell Wire')
                                ? 'bg-sky-100 text-sky-700'
                                : 'bg-grey-100 text-grey-600'
                            }`}>
                              {item.article.content_product_name}
                            </span>
                          )}
                          {/* One-owner coverage: badge the OWNER masthead's copy so
                              reviewers prioritise it. Secondary copies get no badge —
                              absence of PRIMARY communicates it. */}
                          {item.coverage_role === 'primary' && (
                            <span
                              className="inline-flex items-center px-2 py-0.5 rounded text-[10px] font-semibold bg-primary-100 text-primary-700"
                              title="Owner masthead for this story — review this copy first"
                            >
                              PRIMARY
                            </span>
                          )}
                          {item.article.voice_name && (
                            <span className="inline-flex items-center px-2 py-0.5 rounded text-[10px] font-semibold bg-grey-100 text-grey-600">
                              {item.article.voice_name}
                            </span>
                          )}
                          {item.article.persona_name && (
                            <span className="inline-flex items-center px-2 py-0.5 rounded text-[10px] font-semibold bg-primary-50 text-primary-700">
                              {item.article.persona_name}
                            </span>
                          )}
                          <span className="text-xs text-grey-400">{item.article.word_count} words</span>
                          <span className="text-xs text-grey-400">{format(new Date(item.article.generation_timestamp), 'MMM d, yyyy · HH:mm')}</span>
                        </div>
                      </div>

                      {/* Publish badges + live job chip */}
                      <div className="flex items-center gap-1.5 flex-wrap w-full pl-7 sm:w-auto sm:pl-0 sm:flex-shrink-0 sm:flex-nowrap">
                        {item.article.auto_published && (
                          <span
                            className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-semibold bg-grey-200 text-grey-700"
                            title="Published automatically — no operator action"
                          >
                            auto
                          </span>
                        )}
                        {(() => {
                          const jobInfo = articleJobs.get(item.article.article_id);
                          if (!jobInfo) return null;
                          const toneClass =
                            jobInfo.tone === 'publish'
                              ? 'bg-primary-100 text-primary-700 border-primary-200'
                              : jobInfo.tone === 'unpublish'
                              ? 'bg-warning-100 text-warning-700 border-warning-200'
                              : jobInfo.tone === 'promote'
                              ? 'bg-success-100 text-success-700 border-success-200'
                              : 'bg-grey-100 text-grey-700 border-grey-200';
                          return (
                            <span
                              className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-semibold border ${toneClass}`}
                              title={`${jobInfo.job_type} · ${jobInfo.status}`}
                            >
                              <Loader2 className="h-2.5 w-2.5 animate-spin" />
                              {jobInfo.label}
                            </span>
                          );
                        })()}
                        {(() => { const effective = effectivePublishTargets(item.publish_targets); return effective.length > 0 ? (
                          effective.map((pt, idx) => (
                            <span
                              key={`${pt.target_name}-${pt.destination_type}-${idx}`}
                              className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-semibold ${
                                pt.destination_type === 'main_branch'
                                  ? 'bg-success-100 text-success-700'
                                  : 'bg-warning-100 text-warning-700'
                              }`}
                            >
                              <Globe className="h-2.5 w-2.5" />
                              {pt.target_name} {pt.destination_type === 'main_branch' ? 'live' : 'test'}
                            </span>
                          ))
                        ) : (
                          <span
                            className={`text-[10px] px-2 py-0.5 rounded-full font-semibold ${
                              item.article.publish_status === 'approved'
                                ? 'bg-primary-100 text-primary-700'
                                : item.article.publish_status === 'published'
                                ? 'bg-success-100 text-success-700'
                                : 'bg-grey-100 text-grey-500'
                            }`}
                          >
                            {item.article.publish_status}
                          </span>
                        ); })()}
                      </div>

                      {/* Archive button */}
                      {item.article.publish_status !== 'archived' && (
                        <button
                          onClick={(e) => handleArchiveClick(e, item.article)}
                          className="p-1.5 text-grey-300 hover:text-warning-600 hover:bg-warning-50 rounded transition-colors flex-shrink-0"
                          title="Archive article"
                        >
                          <Archive className="h-4 w-4" />
                        </button>
                      )}
                    </Link>
                                  ))}
                              </div>
                            )}
                          </div>
                        );
                      })}
                    </div>
                  );
                })()}
              </div>
            ))}
          </div>

          {/* Pagination */}
          {!searchActive && data.pages > 1 && (
            <div className="bg-surface rounded-xl border border-grey-200 px-8 py-5 flex items-center justify-between shadow-md">
              <div className="text-base text-grey-700 font-semibold">
                Page {page} of {data.pages}
              </div>
              <div className="flex space-x-3">
                <button
                  onClick={() => setPage(page - 1)}
                  disabled={page === 1}
                  className="px-5 py-2.5 border-2 border-grey-300 rounded-lg text-grey-700 font-semibold hover:bg-surface hover:border-primary-500 disabled:opacity-50 disabled:cursor-not-allowed transition-all"
                >
                  <ChevronLeft className="h-5 w-5" />
                </button>
                <button
                  onClick={() => setPage(page + 1)}
                  disabled={page === data.pages}
                  className="px-5 py-2.5 border-2 border-grey-300 rounded-lg text-grey-700 font-semibold hover:bg-surface hover:border-primary-500 disabled:opacity-50 disabled:cursor-not-allowed transition-all"
                >
                  <ChevronRight className="h-5 w-5" />
                </button>
              </div>
            </div>
          )}
        </>
      )}

      {/* Archive / Delete Confirmation Dialog */}
      {actionConfirm && (
        <Modal
          onClose={() => setActionConfirm(null)}
          canClose={!archiveMutation.isPending && !deleteMutation.isPending}
          size="md"
        >
          <ModalBody>
            {actionConfirm.publishStatus === 'published' ? (
              <>
                <h3 className="text-xl font-bold text-grey-900 mb-4">Article is Published</h3>
                <p className="text-grey-600 mb-2">
                  This article is currently published. You must unpublish it before archiving or deleting.
                </p>
                <p className="text-grey-900 font-medium p-3 bg-grey-50 rounded border border-grey-200">
                  {actionConfirm.articleTitle}
                </p>
              </>
            ) : (
              <>
                <h3 className="text-xl font-bold text-grey-900 mb-4">
                  {actionConfirm.permanentDelete ? 'Permanently Delete Article?' : 'Archive Article?'}
                </h3>
                <p className="text-grey-600 mb-2">
                  {actionConfirm.permanentDelete
                    ? 'This will permanently delete this article. This action cannot be undone.'
                    : 'This will archive the article. It can be found later using the archived filter.'}
                </p>
                <p className="text-grey-900 font-medium mb-4 p-3 bg-grey-50 rounded border border-grey-200">
                  {actionConfirm.articleTitle}
                </p>
                <label className="flex items-center space-x-2 text-sm text-grey-600 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={actionConfirm.permanentDelete}
                    onChange={(e) => setActionConfirm({ ...actionConfirm, permanentDelete: e.target.checked })}
                    className="h-4 w-4 rounded border-grey-300 text-danger-600 focus:ring-danger-500"
                  />
                  <span>Permanently delete instead</span>
                </label>
              </>
            )}
          </ModalBody>
          <ModalFooter>
            {actionConfirm.publishStatus === 'published' ? (
              <>
                <button
                  onClick={() => setActionConfirm(null)}
                  className="flex-1 px-4 py-2.5 border-2 border-grey-300 text-grey-700 font-semibold rounded-lg hover:bg-grey-50 transition-colors"
                >
                  Cancel
                </button>
                <button
                  onClick={() => {
                    setActionConfirm(null);
                    navigate(`/articles/${actionConfirm.articleId}`);
                  }}
                  className="flex-1 px-4 py-2.5 bg-primary-500 hover:bg-primary-600 text-white font-semibold rounded-lg transition-colors"
                >
                  Go to Article
                </button>
              </>
            ) : (
              <>
                <button
                  onClick={() => setActionConfirm(null)}
                  disabled={archiveMutation.isPending || deleteMutation.isPending}
                  className="flex-1 px-4 py-2.5 border-2 border-grey-300 text-grey-700 font-semibold rounded-lg hover:bg-grey-50 transition-colors disabled:opacity-50"
                >
                  Cancel
                </button>
                <button
                  onClick={() => {
                    if (actionConfirm.permanentDelete) {
                      deleteMutation.mutate(actionConfirm.articleId);
                    } else {
                      archiveMutation.mutate(actionConfirm.articleId);
                    }
                  }}
                  disabled={archiveMutation.isPending || deleteMutation.isPending}
                  className={`flex-1 px-4 py-2.5 font-semibold rounded-lg transition-colors disabled:opacity-50 ${
                    actionConfirm.permanentDelete
                      ? 'bg-danger-600 hover:bg-danger-700 text-white'
                      : 'bg-warning-500 hover:bg-warning-600 text-white'
                  }`}
                >
                  {archiveMutation.isPending || deleteMutation.isPending
                    ? 'Processing...'
                    : actionConfirm.permanentDelete
                    ? 'Delete Permanently'
                    : 'Archive'}
                </button>
              </>
            )}
          </ModalFooter>
        </Modal>
      )}
      <QualityGateOverrideDialog
        isOpen={!!groupOverride}
        onClose={() => setGroupOverride(null)}
        metadata={groupOverride?.metadata}
        onSubmit={async (reason) => {
          if (groupOverride) await groupDecision.mutateAsync({ action: 'release', ids: groupOverride.ids, reason });
          setGroupOverride(null);
        }}
      />
    </div>
  );
}

// =============================================================================
// Date strip helpers + DateBar — copied verbatim from SocialDraftsPage so the
// Articles strip matches the Socials/Notifications UX 1:1. Worth extracting
// into a shared component when a fourth caller appears.
// =============================================================================

function ymd(d: Date): string {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
}

function todayYMD(): string {
  return ymd(new Date());
}

function shiftDays(date: string, deltaDays: number): string {
  const d = new Date(date + 'T00:00:00');
  d.setDate(d.getDate() + deltaDays);
  return ymd(d);
}

function useDebounced<T>(value: T, delayMs: number): T {
  const [debounced, setDebounced] = useState(value);
  useEffect(() => {
    const id = setTimeout(() => setDebounced(value), delayMs);
    return () => clearTimeout(id);
  }, [value, delayMs]);
  return debounced;
}
